import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, LogOut, LayoutDashboard } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

export const Logo = ({ light, small }) => {
  const { site } = useApp();
  return (
    <Link to="/" data-testid={light ? "footer-brand-logo" : "brand-logo"} className={`flex items-center gap-2 sm:gap-3 min-w-0 ${light ? "text-white" : "text-[#1A1412]"}`}>
      <img src={fileUrl(site?.logo_url || "/logo.png")} alt="Craftendemic" className={`${small ? "w-6 h-6" : "w-7 h-7 sm:w-8 sm:h-8"} object-contain shrink-0 ${light ? "invert" : ""}`} data-testid={light ? "footer-brand-logo-img" : "brand-logo-img"} />
      <span className={small ? "text-[13px] font-semibold tracking-[0.1em] whitespace-nowrap" : "text-sm sm:text-lg font-semibold tracking-[0.12em] sm:tracking-[0.18em] truncate"}>CRAFTENDEMIC</span>
    </Link>
  );
};

const WARRANTY_URL = "https://craftendemic.com/warranty";
const label = (t, k) => (k === "warranty" ? "Warranty" : t(k));
const links = [["/", "home"], ["/shop", "products"], ["/shop?type=course,workshop", "academy"], [WARRANTY_URL, "warranty"], ["/journal", "news"], ["/about", "about"]];

function Toggle({ value, options, onChange, testid }) {
  return (
    <div className="flex items-center border border-[#E6DFD5] text-[11px] font-mono" data-testid={testid}>
      {options.map((o) => (
        <button key={o} data-testid={`${testid}-${o.toLowerCase()}`} onClick={() => onChange(o)}
          className={`px-2 py-1 transition-colors ${value === o ? "bg-[#3B220C] text-white" : "text-[#6E6259] hover:text-[#3B220C]"}`}>{o}</button>
      ))}
    </div>
  );
}

export default function Header() {
  const { t, lang, setLang, currency, setCurrency, cart, user, logout } = useApp();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); setSearchOpen(false); nav(`/shop?q=${encodeURIComponent(q)}`); };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E6DFD5]" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-8 h-16 grid grid-cols-[auto_1fr_auto] lg:grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex items-center gap-3">
          <button className="lg:hidden" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu"><Menu size={22} /></button>
          <div className="hidden lg:flex gap-2">
            <Toggle value={lang.toUpperCase()} options={["ID", "EN"]} onChange={(v) => setLang(v.toLowerCase())} testid="language-switcher" />
            <Toggle value={currency} options={["IDR", "USD"]} onChange={setCurrency} testid="currency-switcher" />
          </div>
        </div>
        <div className="min-w-0 flex justify-center"><Logo /></div>
        <div className="flex items-center justify-end gap-3 sm:gap-5 text-[#1A1412] shrink-0">
          <button onClick={() => setSearchOpen(true)} data-testid="search-open-btn" aria-label="Search"><Search size={20} strokeWidth={1.5} /></button>
          <Link to="/cart" className="relative" data-testid="cart-link" aria-label="Cart">
            <ShoppingBag size={20} strokeWidth={1.5} />
            {cart.count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#8C4F27] text-white text-[10px] w-4 h-4 rounded-full grid place-items-center">{cart.count}</span>}
          </Link>
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-btn" aria-label="Account">
                {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full shrink-0" /> : <User size={20} strokeWidth={1.5} />}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account"><User size={14} className="mr-2" />{t("account")}</DropdownMenuItem>
                {["superadmin", "staff"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin"><LayoutDashboard size={14} className="mr-2" />{t("admin")}</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout"><LogOut size={14} className="mr-2" />{t("logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="login-link" aria-label="Sign in"><User size={20} strokeWidth={1.5} /></Link>
          )}
        </div>
      </div>
      <nav className="hidden lg:flex justify-center gap-10 pb-3 text-[15px]">
        {links.map(([to, k]) => to === WARRANTY_URL ? (
          <a key={k} href={to} data-testid={`nav-${k}`} className="link-underline text-[#1A1412] hover:text-[#8C4F27] transition-colors">{label(t, k)}</a>
        ) : (
          <NavLink key={k} to={to} end data-testid={`nav-${k}`} className="link-underline text-[#1A1412] hover:text-[#8C4F27] transition-colors">{t(k)}</NavLink>
        ))}
      </nav>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="rounded-none max-w-2xl top-[20%] translate-y-0">
          <DialogTitle className="font-serif text-3xl font-normal">{t("search_ph")}</DialogTitle>
          <form onSubmit={submit} className="flex border-b border-[#3B220C]">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} data-testid="search-input"
              className="flex-1 py-3 outline-none bg-transparent text-lg" />
            <button data-testid="search-submit-btn"><Search size={20} /></button>
          </form>
        </DialogContent>
      </Dialog>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 overflow-y-auto">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex flex-col gap-5 mt-8 text-lg">
            {links.map(([to, k]) => to === WARRANTY_URL
              ? <a key={k} href={to} data-testid={`mobile-nav-${k}`}>{label(t, k)}</a>
              : <Link key={k} to={to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${k}`}>{t(k)}</Link>)}
            <div className="border-t border-[#E6DFD5] pt-5 flex flex-col gap-4">
              {user ? (<>
                <Link to="/account" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-account" className="flex items-center gap-3"><User size={18} />{lang === "id" ? "Akun saya" : "My account"}</Link>
                {["superadmin", "staff"].includes(user.role) && <Link to="/admin" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-admin" className="flex items-center gap-3"><LayoutDashboard size={18} />{t("admin")}</Link>}
                <button onClick={async () => { setMenuOpen(false); await logout(); nav("/"); }} data-testid="mobile-nav-logout" className="flex items-center gap-3 text-left"><LogOut size={18} />{t("logout")}</button>
              </>) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-login" className="flex items-center gap-3"><User size={18} />{lang === "id" ? "Masuk / Akun saya" : "Sign in / My account"}</Link>
              )}
            </div>
            <div className="flex gap-2 pt-4">
              <Toggle value={lang.toUpperCase()} options={["ID", "EN"]} onChange={(v) => setLang(v.toLowerCase())} testid="mobile-language-switcher" />
              <Toggle value={currency} options={["IDR", "USD"]} onChange={setCurrency} testid="mobile-currency-switcher" />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
